import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { buildApiUrl } from "@/config/api";
import { useUserToken } from "@/store/userStore";
import { Button } from "@/ui/button";

/**
 * „A bejelentésed elkészült, teszteld és zárd le" figyelmeztető sáv.
 *
 * Miért kell, és miért feltűnő:
 * a fejlesztői bejelentőből eddig CSAK e-mail ment a bejelentőnek, a
 * felületen semmi nem jelezte, hogy egy jegye elkészült. A rendszerben 17
 * jegy állt „Tesztelhető" állapotban hónapokig, mert senki nem tudott róluk.
 * Egy halvány sávot ugyanígy átlépnének, ezért ez teli színnel, ikonnal és
 * darabszámmal jelenik meg, és NEM tűnik el magától: amíg van visszajelzésre
 * váró jegy, minden oldalon ott marad.
 *
 * Visszajelzés egy kattintással, a felületről — ugyanazt a nyilvános
 * végpontot hívja, amit az e-mail két gombja.
 */

type PendingIssue = {
	id: number;
	title: string;
	type: string;
	admin_response: string | null;
	testable_at: string | null;
	feedback_token: string | null;
};

/** Ennyi jegyet mutatunk kinyitás nélkül. */
const LATHATO = 2;

export default function DeveloperIssueBanner() {
	const userToken = useUserToken();
	const navigate = useNavigate();

	const [jegyek, setJegyek] = useState<PendingIssue[]>([]);
	const [mind, setMind] = useState(false);
	const [folyamatban, setFolyamatban] = useState<number | null>(null);

	const betoltes = useCallback(async () => {
		if (!userToken.accessToken) return;

		try {
			const valasz = await fetch(buildApiUrl("/developer-issues/pending-feedback"), {
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${userToken.accessToken}`,
				},
			});
			if (!valasz.ok) return; // néma: a sáv nem akadályozhatja az oldalt

			const eredmeny = await valasz.json();
			setJegyek(Array.isArray(eredmeny?.data) ? eredmeny.data : []);
		} catch {
			// Hálózati hiba esetén nem mutatunk semmit.
		}
	}, [userToken.accessToken]);

	useEffect(() => {
		void betoltes();

		// Ha közben valaki tesztelhetőre állítja a jegyet, ne kelljen
		// újratölteni az oldalt. A lap előtérbe kerülésekor is ellenőrzünk.
		const ido = setInterval(betoltes, 5 * 60 * 1000);
		const elotérbe = () => {
			if (document.visibilityState === "visible") void betoltes();
		};

		document.addEventListener("visibilitychange", elotérbe);

		return () => {
			clearInterval(ido);
			document.removeEventListener("visibilitychange", elotérbe);
		};
	}, [betoltes]);

	const visszajelzes = async (jegy: PendingIssue, eredmeny: "works" | "fails") => {
		if (!jegy.feedback_token) {
			navigate("/developer-issues");

			return;
		}

		setFolyamatban(jegy.id);

		try {
			const valasz = await fetch(buildApiUrl(`/public/developer-issue-feedback/${jegy.feedback_token}/${eredmeny}`), {
				method: "POST",
				headers: { Accept: "application/json", "Content-Type": "application/json" },
				body: JSON.stringify({}),
			});
			if (!valasz.ok) throw new Error();

			// Helyben vesszük ki a sorból: nem kell újratöltés.
			setJegyek((elozo) => elozo.filter((j) => j.id !== jegy.id));
			toast.success(eredmeny === "works" ? "Köszönjük, lezárva." : "Jeleztük, hogy még nem jó.");
		} catch {
			toast.error("A visszajelzés nem sikerült. Próbáld újra, vagy nyisd meg a bejelentést.");
		} finally {
			setFolyamatban(null);
		}
	};

	if (jegyek.length === 0) return null;

	const mutatott = mind ? jegyek : jegyek.slice(0, LATHATO);

	return (
		<div className="mb-4 overflow-hidden rounded-lg border-2 border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-950/40">
			<div className="flex items-center gap-3 bg-amber-500 px-4 py-2.5 text-white">
				<Icon icon="solar:bell-bing-bold" size={22} className="shrink-0" />
				<div className="min-w-0 flex-1">
					<span className="font-semibold">
						{jegyek.length === 1
							? "A bejelentésed elkészült — teszteld és zárd le"
							: `${jegyek.length} bejelentésed elkészült — teszteld és zárd le`}
					</span>
				</div>
				<Button
					variant="ghost"
					size="sm"
					className="shrink-0 text-white hover:bg-amber-600 hover:text-white"
					onClick={() => navigate("/developer-issues")}
				>
					Megnézem
				</Button>
			</div>

			<div className="divide-y divide-amber-200 dark:divide-amber-800">
				{mutatott.map((jegy) => (
					<div key={jegy.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
						<div className="min-w-0 flex-1">
							<div className="font-medium text-amber-900 dark:text-amber-100">{jegy.title}</div>
							{jegy.admin_response && (
								<div className="mt-0.5 whitespace-pre-line text-sm text-amber-800/80 dark:text-amber-200/70">
									{jegy.admin_response}
								</div>
							)}
						</div>
						<div className="flex shrink-0 gap-2">
							<Button size="sm" disabled={folyamatban === jegy.id} onClick={() => visszajelzes(jegy, "works")}>
								<Icon icon="solar:check-circle-bold" size={16} className="mr-1" />
								Működik
							</Button>
							<Button
								size="sm"
								variant="outline"
								disabled={folyamatban === jegy.id}
								onClick={() => visszajelzes(jegy, "fails")}
							>
								Nem működik
							</Button>
						</div>
					</div>
				))}
			</div>

			{jegyek.length > LATHATO && (
				<button
					type="button"
					className="w-full px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
					onClick={() => setMind((e) => !e)}
				>
					{mind ? "Kevesebb" : `További ${jegyek.length - LATHATO} megjelenítése`}
				</button>
			)}
		</div>
	);
}
